import { useEffect, useState } from "react";
import { useChangeBooleans, useComponentProperties, useResetAttributeTable, useResetComponentProperties, useSaveComponent, useUpdateSyncComponentAttributes } from "../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { ATTRIBUTE_PLURAL_LABEL } from "../../Helpers/Labels/AttributeLabel";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import componentPropertiesMap from "./ComponentFormProperties.json";
import AttributeTable from "../AttributeTable/AttributeTable";
import { useTranslation } from "react-i18next";
import DataFilter from "./Shared/DataFilter";

function List() {

    const formProperties = componentPropertiesMap.List;

    // hooks
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const saveComponent = useSaveComponent();
    const updateSyncedComponentAttributes = useUpdateSyncComponentAttributes();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);
    const resetContext = useResetComponentProperties();
    const resetAttributes = useResetAttributeTable();

    const [properties, setProperty] = useComponentProperties();
    const [{
        isGeneralPropertiesChanged,
        isAttributeTableChanged
    }, setChangeProperty] = useChangeBooleans();

    const [labelOrientations, setLabelOrientations] = useState([
        { text: "Top", value: "Top" },
        { text: "Left", value: "Left" }
    ])

    const [propertyColumnOptions, setPropertyColumnOptions] = useState([
        { text: "1", value: "1" },
        { text: "2", value: "2" }
    ]);

    const { t, ready: tReady } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/list",
        "display/tables/attribute"
    ]);

    useEffect(() => {
        if (selectedPage && selectedComponent) {
            setProperties();
            setChangeProperty("isGeneralPropertiesChanged", false);
        }
    }, [selectedPage, selectedComponent])

    useEffect(() => {
        if (tReady) {
            if (accessedFromDesigner) {
                setLabelOrientations([
                    { text: t("label_orientation_options.none", { ns: "display/components/list" }), value: "None" },
                    { text: t("label_orientation_options.left", { ns: "display/components/list" }), value: "Left" },
                    { text: t("label_orientation_options.bottom", { ns: "display/components/list" }), value: "Bottom" },
                    { text: t("label_orientation_options.top", { ns: "display/components/list" }), value: "Top" }
                ]);
            } else {
                setLabelOrientations([
                    { text: t("label_orientation_options.none", { ns: "display/components/list" }), value: "None" },
                    { text: t("label_orientation_options.top", { ns: "display/components/list" }), value: "Top" }
                ]);
            }
            setPropertyColumnOptions([
                { text: t("numbers.1", { ns: "display/common" }), value: "1" },
                { text: t("numbers.2", { ns: "display/common" }), value: "2" }
            ]);
        }
    }, [tReady])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        setIsChanged();
    }, [properties])

    // state
    const [tabSelected, setSelectedTab] = useState(0);

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    function setProperties() {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperty(property, selectedComponent[property])
            }
        }
    }

    function handleInputChange(event) {
        switch (event.target.name) {
            case "labelOrientation":
            case "column":
                setProperty(event.target.name, event.target.value.value);
                break;
            default:
                setProperty(event.target.name, event.target.value);
                break;
        }
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "showFilter":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "filterLabel":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "filterAttributes":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "column":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (
                        selectedComponent[property] !== undefined &&
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
        if (isAttributeTableChanged && selectedComponent.componentsToSync && selectedComponent.componentsToSync.length > 0) {
            updateSyncedComponentAttributes();
        }
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setProperties();
        }
        if (isAttributeTableChanged) {
            resetAttributes();
        }
    }

    function getLabelOrientationValue(value) {
        return labelOrientations.find(orientation => orientation.value === value);
    }

    function getValueFromArray(array, value) {
        return array.find(item => item.value === value);
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1"
                    selected={tabSelected}
                    onSelect={handleTabSelect}
                >
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                <div className="form-label">
                                    {t("form_labels.label_orientation", { ns: "display/components/list" })}
                                </div>
                                <DropDownList
                                    name="labelOrientation"
                                    disabled={selectedComponent.hidden}
                                    onChange={handleInputChange}
                                    className="form-control-custom w-25"
                                    value={getLabelOrientationValue(properties.labelOrientation)}
                                    data={labelOrientations}
                                    textField="text"
                                    dataItemKey="value"
                                />
                                <div className="form-label">
                                    {t("form_labels.num_property_columns", { ns: "display/components/list" })}
                                </div>
                                <DropDownList
                                    name="column"
                                    disabled={selectedComponent.hidden}
                                    onChange={handleInputChange}
                                    className="form-control-custom w-25"
                                    value={getValueFromArray(propertyColumnOptions, properties.column)}
                                    data={propertyColumnOptions}
                                    textField="text"
                                    dataItemKey="value"
                                />
                                <Checkbox
                                    name="showLocalSearch"
                                    disabled={selectedComponent.hidden}
                                    checked={properties.showLocalSearch}
                                    onChange={handleInputChange}
                                    className="my-1 d-block"
                                    label={t("form_labels.local_search", { ns: "display/components/list" })}
                                />
                                <DataFilter />
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={t("tabs.properties", { ns: "display/common" })}>
                        <AttributeTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default List;